import React, { useEffect, useState } from "react";
import { fetchAllResources, fetchGroupPossibleParticipants, fetchReportResources } from "../../services/httpClient";
import html2canvas from "html2canvas";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { ResourcesMetric } from "./charts-metrics/ResourcesMetric";
import { ResourcesAssignedTable } from "./charts-metrics/ResourcesAssignedTable";
import { formatDateTimeToLocal } from "../../helpers/date";
import { signal } from "@preact/signals-react";

export const showResources = signal(true);

const ResourcesTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [resourceData, setResourceData] = useState<any>([]);
  const [consumableTableData, setConsumableTableData] = useState<any>([]);
  const [nonConsumableTableData, setNonConsumableTableData] = useState<any>([]);
  const [populationData, setPopulationData] = useState<any>([]);
  const [reportRunning, setReportRunning] = useState<boolean>(false);

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
    getResources();
    getResourceAssignments(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
    getPopulation();
  }, []);

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const shapeTableData = (resources: any[], resourceType: string) => {
    const data = resources.filter((r) => r.resource_type_key === resourceType);

    const grouped = data.reduce(
      (acc, item) => {
        const key = `${item.assigned_at}-${item.resource_id}-${item.assignee_id}`;
        const localDateTime = formatDateTimeToLocal(item.assigned_at);

        if (!acc[key]) {
          acc[key] = {
            assigned_at: item.assigned_at,
            assigned_at_formatted: localDateTime.date + " " + localDateTime.time,
            resource_id: item.resource_id,
            resource_name: item.resource_name,
            resource_category: item.resource_category,
            assignee_id: item.assignee_id,
            assignee_name: item.assignee_name,
            quantity: 0,
          };
        }

        acc[key].quantity += 1;

        return acc;
      },
      {} as Record<string, any>,
    );

    return Object.values(grouped).sort((a: any, b: any) => new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime());
  };

  const getResources = () => {
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data;
        showResources.value = resources.length > 0 ? true : false;
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getResourceAssignments = (start: string, end: string) => {
    fetchReportResources(start, end)
      .then((res: any) => {
        const resources = res[0].data;
        setResourceData(resources);
        setConsumableTableData(shapeTableData(resources, "consumable"));
        setNonConsumableTableData(shapeTableData(resources, "nonconsumable"));
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getPopulation = () => {
    fetchGroupPossibleParticipants("0")
      .then((res: any) => {
        const population = res[0].data[0];
        setPopulationData(population);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const downloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("resources-section");
    const reportTitle = document.getElementById("resources-title");
    const refreshButton = document.getElementById("resources-refresh-button");
    const downloadConsumablesButton = document.getElementById("download-consumables-button");
    const downloadNonConsumablesButton = document.getElementById("download-nonconsumables-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "hidden";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
      if (refreshButton) refreshButton.style.visibility = "visible";
      if (downloadConsumablesButton) downloadConsumablesButton.style.visibility = "visible";
      if (downloadNonConsumablesButton) downloadNonConsumablesButton.style.visibility = "visible";
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="resources-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="resources-title" className="mb-2">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Resources</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => downloadReport()}>
                  <FontAwesomeIcon icon={faDownload} />
                  {" Report"}
                </Button>
              )}
            </Col>
          </Row>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getResources}
            hideBtnId="resources-refresh-button"
          />
          <Row className="mt-3 mb-3">
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="people" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="resources" resourceType="consumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="resources" resourceType="nonconsumable" />
            </Col>
            <Col xs={3} className="p-2">
              <ResourcesMetric data={resourceData} metricType="usage" population={populationData} />
            </Col>
          </Row>
          <ResourcesAssignedTable data={consumableTableData} resourceType="consumable" />
          <ResourcesAssignedTable data={nonConsumableTableData} resourceType="nonconsumable" />
        </>
      )}
    </div>
  );
};

export default ResourcesTab;
